<template>
  <div class="admin">
    <!-- Sidebar -->
    <aside class="sidebar">
      <div class="logo">
        <img src="@/assets/logo.png" alt="Logo" />
        <span>{{ $t('app.name') }}</span>
      </div>

      <nav>
        <router-link to="/admin" class="nav-item" exact>
          <i class="fas fa-chart-line"></i>
          <span>{{ $t('admin.dashboard.title') }}</span>
        </router-link>
        
        <router-link to="/admin/stores" class="nav-item">
          <i class="fas fa-store"></i>
          <span>{{ $t('admin.stores.title') }}</span>
        </router-link>
        
        <router-link to="/admin/employees" class="nav-item">
          <i class="fas fa-users"></i>
          <span>{{ $t('admin.employees.title') }}</span>
        </router-link>
      </nav>

      <div class="user">
        <span>admin@kissmywheels.com</span>
        <button @click="logout">
          <i class="fas fa-sign-out-alt"></i>
          {{ $t('app.logout') }}
        </button>
      </div>
    </aside>

    <!-- Main -->
    <main class="main">
      <header>
        <h1>{{ pageTitle }}</h1>
        <div class="actions">
          <LanguageSelector />
        </div>
      </header>

      <div class="content">
        <router-view></router-view>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const pageTitle = computed(() => {
  switch (route.path) {
    case '/admin': return t('admin.dashboard.title')
    case '/admin/stores': return t('admin.stores.title')
    case '/admin/employees': return t('admin.employees.title')
    default: return t('admin.title')
  }
})

const logout = () => router.push('/login')

const menuItems = [
  {
    icon: 'fas fa-chart-line',
    label: t('admin.menu.dashboard'),
    route: { name: 'admin-dashboard' }
  },
  {
    icon: 'fas fa-store',
    label: t('admin.menu.stores'),
    route: { name: 'admin-stores' }
  },
  {
    icon: 'fas fa-users',
    label: t('admin.menu.employees'),
    route: { name: 'admin-employees' }
  },
  {
    icon: 'fas fa-calendar-alt',
    label: t('admin.menu.appointments'),
    route: { name: 'admin-appointments' }
  }
];
</script>

<style lang="scss" scoped>
.admin {
  display: grid;
  grid-template-columns: 260px 1fr;
  min-height: 100vh;
}

.sidebar {
  background: #1e293b;
  color: white;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  .logo {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 24px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    
    img {
      width: 32px;
      height: 32px;
    }
    
    span {
      font-size: 18px;
      font-weight: 600;
    }
  }

  nav {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .nav-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      color: rgba(255,255,255,0.7);
      text-decoration: none;
      border-radius: 8px;
      transition: 0.2s;

      &:hover {
        background: rgba(255,255,255,0.1);
        color: white;
      }

      &.router-link-active {
        background: #3b82f6;
        color: white;
      }

      i {
        width: 20px;
        font-size: 16px;
      }
    }
  }

  .user {
    margin-top: auto;
    padding-top: 24px;
    border-top: 1px solid rgba(255,255,255,0.1);
    display: flex;
    flex-direction: column;
    gap: 12px;

    span {
      font-size: 14px;
      opacity: 0.7;
    }

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 12px;
      background: rgba(255,255,255,0.1);
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: 0.2s;

      &:hover {
        background: rgba(255,255,255,0.2);
      }
    }
  }
}

.main {
  background: #f1f5f9;

  header {
    background: white;
    padding: 24px 32px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h1 {
      font-size: 24px;
      font-weight: 600;
      color: #1e293b;
    }

    .actions {
      select {
        padding: 8px 32px 8px 12px;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        background: white;
        font-size: 14px;
        color: #1e293b;
        cursor: pointer;
        
        &:focus {
          border-color: #3b82f6;
          outline: none;
        }
      }
    }
  }

  .content {
    padding: 32px;
    height: calc(100vh - 81px);
    overflow: auto;
  }
}
</style> 